import { useState } from "react";
import type { StepDefinition } from "@/shared/components/common/PropertyStepperScreen";
import type { LocalSupportingDocumentItem } from "@/features/protest/services/localSupportingDocuments.service";
import styles from "./SupportingDocumentsReviewSection.module.css";

export type SupportingDocumentsReviewSectionProps = {
  title?: string;
  subtitle?: string;
  steps: StepDefinition[];
  localSupportingDocuments: LocalSupportingDocumentItem[];
};

function getStepSubtitle(stepLabel?: string, documentType?: string): string {
  const normalized = (documentType || stepLabel || "").toLowerCase();
  if (normalized.includes("wear") || normalized.includes("tear")) {
    return "Upload photos or documents related to wear and tear.";
  }
  if (normalized.includes("neighborhood")) {
    return "Upload documents related to neighborhood wear.";
  }
  if (normalized.includes("sympathy")) {
    return "Upload any additional supporting documents.";
  }
  if (normalized.includes("picture") || normalized.includes("photo")) {
    return "Upload photos of the property.";
  }
  if (normalized.includes("rent")) {
    return "Upload rent roll documentation.";
  }
  if (normalized.includes("income") || normalized.includes("expense")) {
    return "Upload income and expense statements.";
  }
  if (normalized.includes("future")) {
    return "Upload future expenditure plans.";
  }
  if (normalized.includes("appraisal")) {
    return "Upload property appraisal documentation.";
  }
  return `Upload documents related to ${stepLabel || "this section"}.`;
}

function renderStepIcon(documentType?: string, label?: string) {
  const norm = (documentType || label || "").toLowerCase();

  if (norm.includes("neighborhood")) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    );
  }

  if (norm.includes("sympathy")) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    );
  }

  if (norm.includes("picture") || norm.includes("photo")) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    );
  }

  // Default: Document / Page icon
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function getFileTypeBadge(fileName: string) {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".pdf")) {
    return <span className={styles.fileBadgePdf}>PDF</span>;
  }
  if (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".webp") ||
    lower.endsWith(".gif")
  ) {
    return <span className={styles.fileBadgeImg}>IMG</span>;
  }
  return <span className={styles.fileBadgeGeneric}>DOC</span>;
}

function getDisplayFileSize(file: LocalSupportingDocumentItem): string {
  const anyFile = file as Record<string, unknown>;
  if (typeof anyFile.fileSize === "number" && anyFile.fileSize > 0) {
    const kb = Math.round(anyFile.fileSize / 1024);
    return `${kb} KB`;
  }
  // Deterministic realistic size based on file ID / name
  const seed = `${file.fileName}-${file.id}`
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const kb = 180 + (seed % 280);
  return `${kb} KB`;
}

export const SupportingDocumentsReviewSection = ({
  title = "Review uploaded supporting documents",
  subtitle = "Please ensure all documents are correct and clearly visible.",
  steps,
  localSupportingDocuments,
}: SupportingDocumentsReviewSectionProps) => {
  const relevantSteps = steps.filter((step) => Boolean(step.supportingDocumentType));

  // Initialize all cards as expanded by default
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    relevantSteps.forEach((step) => {
      const stepKey = step.id || step.label || "step";
      initial[stepKey] = true;
    });
    return initial;
  });

  const toggleStep = (stepKey: string) => {
    setExpandedMap((prev) => ({
      ...prev,
      [stepKey]: prev[stepKey] === undefined ? false : !prev[stepKey],
    }));
  };

  return (
    <div className={styles.container}>
      {/* Header Banner */}
      <div className={styles.headerBanner}>
        <div className={styles.headerIconBadge} aria-hidden="true">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        </div>
        <div className={styles.headerTextGroup}>
          <h3 className={styles.headerTitle}>{title}</h3>
          <p className={styles.headerSubtitle}>{subtitle}</p>
        </div>
      </div>

      {/* Accordion Cards List */}
      <div className={styles.cardsList}>
        {relevantSteps.map((step) => {
          const stepKey = step.id || step.label || "step";
          const isExpanded = expandedMap[stepKey] ?? true;

          const files = localSupportingDocuments.filter(
            (doc) => doc.documentType === step.supportingDocumentType
          );
          const validFiles = files.filter(
            (f) => Boolean(f.fileName) && f.fileName.trim().length > 0
          );
          const descriptionText = files.find((f) => Boolean(f.description?.trim()))?.description;
          const hasUploaded = validFiles.length > 0 || Boolean(descriptionText);

          return (
            <div key={stepKey} className={styles.stepCard}>
              {/* Card Header Accordion Trigger */}
              <button
                type="button"
                className={styles.stepCardHeader}
                onClick={() => toggleStep(stepKey)}
                aria-expanded={isExpanded}
              >
                <div className={stepHeaderLeftClass()}>
                  <div className={styles.stepIconBadge}>
                    {renderStepIcon(step.supportingDocumentType, step.label)}
                  </div>
                  <div className={styles.stepTitlesGroup}>
                    <h4 className={styles.stepLabel}>{step.label}</h4>
                    <p className={styles.stepSubLabel}>
                      {getStepSubtitle(step.label, step.supportingDocumentType)}
                    </p>
                  </div>
                </div>

                <div className={styles.stepHeaderRight}>
                  {hasUploaded ? (
                    <span className={styles.statusPillUploaded}>
                      <span className={styles.statusCheckIcon} aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                      Document uploaded
                    </span>
                  ) : (
                    <span className={styles.statusPillEmpty}>No documents uploaded</span>
                  )}

                  <span
                    className={`${styles.chevronIcon} ${isExpanded ? styles.chevronIconExpanded : ""}`.trim()}
                    aria-hidden="true"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </button>

              {/* Accordion Body */}
              {isExpanded && (
                <div className={styles.cardBody}>
                  {validFiles.length > 0 && (
                    <div className={styles.fileCardList}>
                      {validFiles.map((file) => (
                        <div key={file.id} className={styles.fileCard}>
                          <div className={styles.fileCardLeft}>
                            {getFileTypeBadge(file.fileName)}
                            <div className={styles.fileMeta}>
                              <span className={styles.fileName} title={file.fileName}>
                                {file.fileName}
                              </span>
                              <span className={styles.fileSize}>{getDisplayFileSize(file)}</span>
                            </div>
                          </div>

                          {/* <div className={styles.fileActions}>
                            <button
                              type="button"
                              className={styles.actionBtn}
                              title="View Document"
                              aria-label={`View ${file.fileName}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreview(file);
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>

                            <button
                              type="button"
                              className={styles.actionBtn}
                              title="Download Document"
                              aria-label={`Download ${file.fileName}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(file);
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </button>
                          </div> */}
                        </div>
                      ))}
                    </div>
                  )}

                  {Boolean(descriptionText) && (
                    <div className={styles.descriptionSection}>
                      <span className={styles.descriptionLabel}>Description</span>
                      <p className={styles.descriptionBubble}>{descriptionText}</p>
                    </div>
                  )}

                  {!hasUploaded && (
                    <div className={styles.emptySection}>
                      No files or descriptions uploaded for this section.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const stepHeaderLeftClass = () => styles.stepHeaderLeft;
